import {
	ExchangeAccountModel,
	getFolderBreadcrumbs,
	IFolderViewModel,
	LazyLoadedChildren,
	listFolders,
	MailFolderBreadcrumbItemModel,
	MailFolderOrRootLocationModel
} from "client/Api/Email/email";
import {
	MailMessageLocationModel,
	MailMessageLocationQueryModel
} from "client/Api/Email/common";
import { ObjectWithClientId, setClientId } from "client/Misc/clientId";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { isNil, isNotNil } from "client/Misc/utils";

export type ExchangeAccountItem = {
	kind: "account";
	account: ObjectWithClientId<ExchangeAccountModel>;
} & ({
	isFaulted: true;
} | {
	isFaulted: false;
	folders: readonly ExchangeFolderItem[];
});

export type ExchangeFolderChildrentState =
	| { status: "pending" | "loading" | "error"; }
	| {
		status: "success";
		children: readonly ExchangeFolderItem[];
	};

export type ExchangeFolderItem = {
	kind: "folder";
	folder: IFolderViewModel<LazyLoadedChildren>;
	smtpAddress: string;
	childrenState: ExchangeFolderChildrentState;
};

export type ExchangeItem =
	| ExchangeAccountItem
	| ExchangeFolderItem;

export function convertToExchangeAccountItem(
	account: ExchangeAccountModel
): ExchangeAccountItem {
	const {
		isFaulted,
		folders
	} = account;

	const accountWitchClientId: ObjectWithClientId<ExchangeAccountModel> = setClientId(account);

	if (isFaulted) {
		return {
			kind: "account",
			account: accountWitchClientId,
			isFaulted: true
		};
	}

	return {
		kind: "account",
		account: accountWitchClientId,
		isFaulted: false,
		folders: folders.map(
			x => convertToExchangeFolderItem(
				account.emailAddress,
				x
			)
		)
	};
}

export function convertToExchangeFolderItem(
	smtpAddress: string,
	folder: IFolderViewModel<LazyLoadedChildren>
): ExchangeFolderItem {
	return {
		kind: "folder",
		smtpAddress,
		folder,
		childrenState: { status: "pending" }
	};
}

export function updateChildrenState(
	smtpAddress: string,
	targetFolderId: string,
	childrenState: ExchangeFolderChildrentState,
	accountItems: readonly ExchangeAccountItem[]
): readonly ExchangeAccountItem[] {
	let hasChanged = false;

	const result: readonly ExchangeAccountItem[] = accountItems
		.map(
			account => {
				if (account.account.emailAddress !== smtpAddress || account.isFaulted) {
					return account;
				}

				const updatedFolders: readonly ExchangeFolderItem[] = updateFolderItemsRecursive(
					targetFolderId,
					childrenState,
					account.folders
				);

				// Compare references
				if (updatedFolders === account.folders) {
					return account;
				}

				hasChanged = true;
				return {
					...account,
					folders: updatedFolders
				};
			}
		);

	return hasChanged
		? result
		: accountItems;
}

function updateFolderItemsRecursive(
	targetFolderId: string,
	childrenState: ExchangeFolderChildrentState,
	folders: readonly ExchangeFolderItem[]
): readonly ExchangeFolderItem[] {
	let hasChanged = false;

	const result: readonly ExchangeFolderItem[] = folders
		.map(
			item => {
				if (item.folder.folderId.id === targetFolderId) {
					hasChanged = true;

					return {
						...item,
						childrenState
					};
				}

				if (item.childrenState.status === "success") {
					const updatedChildren: readonly ExchangeFolderItem[] = updateFolderItemsRecursive(
						targetFolderId,
						childrenState,
						item.childrenState.children
					);

					if (updatedChildren !== item.childrenState.children) {
						hasChanged = true;

						return {
							...item,
							childrenState: {
								...item.childrenState,
								children: updatedChildren,
							},
						};
					}
				}

				return item;
			}
		);

	return hasChanged
		? result
		: folders;
}

export type FolderBranch = {
	folder: IFolderViewModel<LazyLoadedChildren>;
	branch: readonly IFolderViewModel<LazyLoadedChildren>[];
};

export function tryToFindFolder(
	accountItems: readonly ExchangeAccountItem[],
	smtpAddress: string,
	targetFolderId: string
): TryResult<FolderBranch> {
	for (const account of accountItems) {
		if (account.isFaulted || account.account.emailAddress !== smtpAddress) {
			continue;
		}

		const result: TryResult<FolderBranch> = searchFolderItemsRecursive(
			targetFolderId,
			account.folders
		);

		if (result.wasSuccessful) {
			return result;
		}
	}

	return { wasSuccessful: false };
}

function searchFolderItemsRecursive(
	targetFolderId: string,
	items: readonly ExchangeFolderItem[]
): TryResult<FolderBranch> {
	for (const item of items) {
		if (item.folder.folderId.id === targetFolderId) {
			return {
				wasSuccessful: true,
				result: {
					folder: item.folder,
					branch: [item.folder]
				}
			};
		}

		if (item.childrenState.status === "success") {
			const childResult: TryResult<FolderBranch> = searchFolderItemsRecursive(
				targetFolderId,
				item.childrenState.children
			);

			if (childResult.wasSuccessful) {
				return {
					wasSuccessful: true,
					result: {
						folder: childResult.result.folder,
						branch: [
							item.folder,
							...childResult.result.branch
						]
					}
				};
			}
		}
	}

	return { wasSuccessful: false };
}

export function tryToFindFolderItem(
	accountItems: readonly ExchangeAccountItem[],
	smtpAddress: string,
	targetFolderId: string
): TryResult<ExchangeFolderItem> {
	for (const account of accountItems) {
		if (account.isFaulted
			|| account.account.emailAddress !== smtpAddress
		) {
			continue;
		}

		const result: TryResult<ExchangeFolderItem> = searchFolderItemRecursive(
			targetFolderId,
			account.folders
		);

		if (result.wasSuccessful) {
			return result;
		}
	}

	return { wasSuccessful: false };
}

function searchFolderItemRecursive(
	targetFolderId: string,
	items: readonly ExchangeFolderItem[]
): TryResult<ExchangeFolderItem> {
	for (const item of items) {
		if (item.folder.folderId.id === targetFolderId) {
			return { wasSuccessful: true, result: item };
		}

		if (item.childrenState.status === "success") {
			const childResult: TryResult<ExchangeFolderItem> = searchFolderItemRecursive(
				targetFolderId,
				item.childrenState.children
			);

			if (childResult.wasSuccessful) {
				return childResult;
			}
		}
	}

	return { wasSuccessful: false };
}

export function getCachedChildren(
	accountItems: readonly ExchangeAccountItem[],
	smtpAddress: string,
	folderId: string | undefined
): TryResult<readonly IFolderViewModel<LazyLoadedChildren>[]> {
	if (isNil(folderId)) {
		const account: ExchangeAccountItem | undefined = accountItems.find(
			x => x.account.emailAddress === smtpAddress
		);

		if (isNil(account) || account.isFaulted) {
			return { wasSuccessful: false };
		}

		return {
			wasSuccessful: true,
			result: account
				.folders
				.map(x => x.folder)
		};
	}

	const parentItemResult: TryResult<ExchangeFolderItem> = tryToFindFolderItem(
		accountItems,
		smtpAddress,
		folderId
	);

	if (!parentItemResult.wasSuccessful
		|| parentItemResult
			.result
			.childrenState
			.status !== "success"
	) {
		return { wasSuccessful: false };
	}

	return {
		wasSuccessful: true,
		result: parentItemResult
			.result
			.childrenState
			.children
			.map(x => x.folder)
	};
}

export async function* fetchFolders(
	accountItems: readonly ExchangeAccountItem[],
	smtpAddress: string,
	folderId: string,
	shouldExcludeSelf: boolean,
	signal?: AbortSignal
): AsyncIterable<readonly ExchangeAccountItem[]> {
	try {
		const folderIdsToLoad: readonly string[] = await resolveFolderIdsToLoad(
			accountItems,
			smtpAddress,
			folderId,
			shouldExcludeSelf,
			signal
		);

		for (const folderIdToLoad of folderIdsToLoad) {
			yield accountItems = updateChildrenState(
				smtpAddress,
				folderIdToLoad,
				{ status: "loading" },
				accountItems
			);

			try {
				const folders: readonly IFolderViewModel<LazyLoadedChildren>[] = await listFolders(
					{
						smtpAddress: {
							isSet: true,
							value: smtpAddress
						},
						folderId: {
							isSet: true,
							value: folderIdToLoad
						}
					},
					signal
				);

				yield accountItems = updateChildrenState(
					smtpAddress,
					folderIdToLoad,
					{
						status: "success",
						children: folders.map(
							x => convertToExchangeFolderItem(
								smtpAddress,
								x
							)
						)
					},
					accountItems
				);
			} catch (e) {
				if (isCancelledError(e)) {
					throw e;
				}

				console.error(e, `Could not retrieve children of folder with id ${folderIdToLoad}`);

				yield accountItems = updateChildrenState(
					smtpAddress,
					folderIdToLoad,
					{ status: "error" },
					accountItems
				);

				break;
			}
		}
	} catch (e) {
		if (isCancelledError(e)) {
			throw e;
		}

		console.error(e, `Folder with id ${folderId} was not found`);
	}
}

async function resolveFolderIdsToLoad(
	accountItems: readonly ExchangeAccountItem[],
	smtpAddress: string,
	targetFolderId: string,
	shouldExcludeSelf: boolean,
	signal?: AbortSignal
): Promise<readonly string[]> {
	const folderResult: TryResult<FolderBranch> = tryToFindFolder(
		accountItems,
		smtpAddress,
		targetFolderId
	);

	let candidateFolderIdsToLoad: readonly string[];

	if (folderResult.wasSuccessful) {
		candidateFolderIdsToLoad = [targetFolderId];
	} else {
		const breadCrumbs: readonly MailFolderBreadcrumbItemModel[] = await getFolderBreadcrumbs(
			{
				smtpAddress,
				folderId: targetFolderId
			},
			signal
		);

		candidateFolderIdsToLoad = breadCrumbs.map(x => x.folderId.id);
	}

	const folderIdsToLoad: string[] = [];

	for (const folderId of candidateFolderIdsToLoad) {
		if (shouldExcludeSelf && folderId === targetFolderId) {
			continue;
		}

		const cachedFoldersResult: TryResult<readonly IFolderViewModel<LazyLoadedChildren>[]> = getCachedChildren(
			accountItems,
			smtpAddress,
			folderId
		);

		if (cachedFoldersResult.wasSuccessful) {
			continue;
		}

		folderIdsToLoad.push(folderId);
	}

	return folderIdsToLoad;
}

export function findNextFolderToSelect(
	accountItems: readonly ExchangeAccountItem[],
	smtpAddress: string,
	folderId: string
): IFolderViewModel<LazyLoadedChildren> {
	const folderResult: TryResult<FolderBranch> = tryToFindFolder(
		accountItems,
		smtpAddress,
		folderId
	);

	if (!folderResult.wasSuccessful
		|| folderResult
			.result
			.branch
			.length === 0
	) {
		throw new Error(`There is no folder to select, after deleting the folder with id ${folderId}`);
	}

	const { branch } = folderResult.result;

	const parentFolder: IFolderViewModel<LazyLoadedChildren> | undefined = branch[branch.length - 2];

	const siblingsResult: TryResult<readonly IFolderViewModel<LazyLoadedChildren>[]> = getCachedChildren(
		accountItems,
		smtpAddress,
		parentFolder
			?.folderId.id
	);

	if (!siblingsResult.wasSuccessful) {
		throw new Error(`Could not find the folders next to the folder with id ${folderId} in mailbox ${smtpAddress}`);
	}

	const siblings: readonly IFolderViewModel<LazyLoadedChildren>[] = siblingsResult.result;

	const folderIndex: number = siblings.findIndex(
		f => f.folderId.id === folderId
	);

	const sibling: IFolderViewModel<LazyLoadedChildren> | undefined = siblings[folderIndex - 1]
		?? siblings[folderIndex + 1];

	if (isNotNil(sibling)) {
		return sibling;
	}

	if (isNil(parentFolder)) {
		throw new Error(`There is no folder to select, after deleting the folder with id ${folderId}`);
	}

	return parentFolder;
}

export function isFolderUnderDeleteFolder(
	accountItems: readonly ExchangeAccountItem[],
	smtpAddress: string,
	folderId: string
): boolean {
	const findFolderResult: TryResult<FolderBranch> = tryToFindFolder(
		accountItems,
		smtpAddress,
		folderId
	);

	if (!findFolderResult.wasSuccessful) {
		throw new Error(`Could not find folder with id ${folderId}`);
	}

	return findFolderResult
		.result
		.branch
		.some(folder => folder.isDeleted);
}

export function convertToMailMessageLocationQueryModel(
	location: MailMessageLocationModel
): MailMessageLocationQueryModel {
	const {
		smtpAddress,
		messageId
	} = location;

	return {
		smtpAddress: smtpAddress.isSet
			? smtpAddress.value
			: null,
		messageId
	};
}

export function convertToLocationModel(
	smtpAddress: string,
	folderId: string
): MailFolderOrRootLocationModel {
	return {
		smtpAddress: {
			isSet: true,
			value: smtpAddress
		},
		folderId: {
			isSet: true,
			value: folderId
		}
	};
}